import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { apiService } from '../services/api';
import { AuthShell } from './Login';

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<ResetPasswordForm>();

  const onSubmit = async (data: ResetPasswordForm) => {
    try {
      setError('');
      await apiService.resetPassword(token, data.password);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <div className="space-y-2 mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Invalid reset link</h1>
          <p className="text-sm text-muted-foreground">This password reset link is missing or malformed.</p>
        </div>
        <Link
          to="/forgot-password"
          className="w-full h-10 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80 flex items-center justify-center"
        >
          Request a new link
        </Link>
      </AuthShell>
    );
  }

  if (success) {
    return (
      <AuthShell>
        <div className="space-y-2 mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Password updated</h1>
          <p className="text-sm text-muted-foreground">Your password has been reset. You can now sign in.</p>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="w-full h-10 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80"
        >
          Back to sign in
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="space-y-2 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Choose a new password</h1>
        <p className="text-sm text-muted-foreground">Enter a new password for your account</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="password">New password</label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Must be at least 8 characters' },
            })}
          />
          {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="confirmPassword">Confirm password</label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) => value === watch('password') || 'Passwords do not match',
            })}
          />
          {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>}
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-10 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-40 mt-2"
        >
          {isSubmitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </AuthShell>
  );
}
